import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import { DateTimeService, ExpandableCard, ExpandableCardProperty, FormattingService } from '@shared';
import { SampleTypeProvider, TableService, TableTypeProvider } from '@domain/business-layer';
import { BaseFileExporterService } from './base-file-exporter.service';
import { FileExporterOption, TableExpandableCards, WorkBookFileData } from '../models';
import { DataRepository } from '@database/workspace';
import { FileWriterService } from './file-writer.service';
import { TimeDataFormattingService } from './time-data-formatting.service';

@Injectable()
export class ExcelFileExporterService extends BaseFileExporterService {
	private WORKSHEET_NAME_LENGTH: number = 31;

	public constructor(
		private fileWriterService: FileWriterService,
		protected tableTypeProvider: TableTypeProvider,
		protected sampleTypeProvider: SampleTypeProvider,
		protected tableService: TableService,
		protected dataRepository: DataRepository,
		protected formattingService: FormattingService,
		protected dateTimeService: DateTimeService,
		protected timeDataFormattingService: TimeDataFormattingService
	) {
		super(
			tableTypeProvider,
			sampleTypeProvider,
			tableService,
			dataRepository,
			formattingService,
			dateTimeService,
			timeDataFormattingService
		);
	}

	public async exportData(tablesExpandableCards: TableExpandableCards[], fileName: string): Promise<void> {
		const workBook: XLSX.WorkBook = XLSX.utils.book_new();

		tablesExpandableCards.forEach((tableExpandableCards: TableExpandableCards) => {
			const expandableCardsProperties: ExpandableCardProperty[][] = tableExpandableCards.expandableCards.map(
				(expandableCard: ExpandableCard) => expandableCard.properties
			);
			const itemsMappedInXlsxForm: { [key: string]: string }[] =
				this.mapItemsInWorksheetFormat(expandableCardsProperties);

			const workSheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(itemsMappedInXlsxForm);
			// This limitation is needed because new excel versions limit the length of a sheet name to 31 chars.
			// https://stackoverflow.com/questions/3681868/is-there-a-limit-on-an-excel-worksheets-name-length
			const workSheetName =
				tableExpandableCards.tableName.length <= this.WORKSHEET_NAME_LENGTH
					? tableExpandableCards.tableName
					: `${tableExpandableCards.tableName.substring(0, 29)}..`;
			XLSX.utils.book_append_sheet(workBook, workSheet, workSheetName);
		});

		const xlsxFileData: WorkBookFileData = { name: fileName, file: workBook };
		this.fileWriterService.writeFile([xlsxFileData], FileExporterOption.xlsx);
	}
}
